import { Box, Paper, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState, type FC } from "react";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { fetchStatisticsVolunteerInsights } from "../statisticsVolunteerInsightsApi";
import {
  buildStatisticsQueryFilters,
  type StatisticsFilters,
  type StatisticsVolunteerAgeGenderRow,
  type StatisticsVolunteerInsightsQueryBody,
} from "../types";
import { KpiPieChart, type PieDatum } from "./KpiPieChart";

interface StatisticsVolunteerInsightsSectionProps {
  filters: StatisticsFilters;
  /** When false, skips the network request (used to defer behind KPI load). */
  enabled?: boolean;
}

function defaultFrom(): Dayjs {
  return dayjs().subtract(29, "day").startOf("day");
}

function defaultTo(): Dayjs {
  return dayjs().startOf("day");
}

const cardSx = {
  flex: 1,
  minWidth: 0,
  p: 2,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
} as const;

function normalizeGenderLabel(gender: string) {
  // Keep UI resilient to enum/localization differences.
  const g = String(gender).toLowerCase();
  if (g.includes("female") || g.includes("נקב")) return "נקבה";
  if (g.includes("male") || g.includes("זכר")) return "זכר";
  return "אחר";
}

export const StatisticsVolunteerInsightsSection: FC<
  StatisticsVolunteerInsightsSectionProps
> = ({ filters, enabled = true }) => {
  const { currOrganization } = useOrganization();
  const [from, setFrom] = useState<Dayjs>(defaultFrom);
  const [to, setTo] = useState<Dayjs>(defaultTo);

  const { dateFrom, dateTo } = useMemo(() => {
    let a = from.startOf("day");
    let b = to.startOf("day");
    if (a.isAfter(b)) {
      const t = a;
      a = b;
      b = t;
    }
    return {
      dateFrom: a.format("YYYY-MM-DD"),
      dateTo: b.format("YYYY-MM-DD"),
    };
  }, [from, to]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "statistics-volunteer-insights",
      currOrganization?.id,
      filters,
      dateFrom,
      dateTo,
    ],
    queryFn: () => {
      const base = buildStatisticsQueryFilters(currOrganization!.id, filters);
      const body: StatisticsVolunteerInsightsQueryBody = {
        ...base,
        dateFrom,
        dateTo,
      };
      return fetchStatisticsVolunteerInsights(body);
    },
    enabled: enabled && !!currOrganization?.id,
    staleTime: 1000 * 60 * 2,
  });

  const { genderPie, agePie } = useMemo(() => {
    const rows = (data?.ageGender ?? []) as StatisticsVolunteerAgeGenderRow[];
    const byBucket = new Map<string, { bucketLabel: string; count: number; }>();
    const byGender = new Map<string, number>();

    for (const r of rows) {
      const bucket = r.bucketKey;
      const existingBucket = byBucket.get(bucket) ?? {
        bucketLabel: r.bucketLabel,
        count: 0,
      };
      existingBucket.count += Number(r.count ?? 0);
      byBucket.set(bucket, existingBucket);

      const g = normalizeGenderLabel(r.gender);
      byGender.set(g, (byGender.get(g) ?? 0) + Number(r.count ?? 0));
    }

    const order = [
      "under18",
      "18_24",
      "25_34",
      "35_44",
      "45_54",
      "55_64",
      "65plus",
    ];

    const agePie: PieDatum[] = order
      .map((k) => byBucket.get(k) ?? null)
      .filter(Boolean)
      .map((v) => ({ name: v!.bucketLabel, value: v!.count }));

    const genderOrder = ["זכר", "נקבה", "אחר"];
    const genderPie: PieDatum[] = genderOrder.map((g) => ({
      name: g,
      value: byGender.get(g) ?? 0,
    }));

    return { genderPie, agePie };
  }, [data?.ageGender]);

  const repeatPie: PieDatum[] = useMemo(() => {
    return (data?.repeat ?? []).map((r) => ({
      name: r.bucketLabel,
      value: Number(r.count ?? 0),
    }));
  }, [data?.repeat]);

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight={600} color="primary">
        סטטיסטיקות מתנדבים
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
      >
        <DatePicker
          label="מתאריך"
          value={from}
          onChange={(v) => v && setFrom(v.startOf("day"))}
          maxDate={dayjs()}
          slotProps={{
            textField: { size: "small" },
            popper: { className: "date-picker-dialog" },
            layout: { className: "date-field-ltr" },
          }}
        />
        <DatePicker
          label="עד תאריך"
          value={to}
          onChange={(v) => v && setTo(v.startOf("day"))}
          maxDate={dayjs()}
          slotProps={{
            textField: { size: "small" },
            popper: { className: "date-picker-dialog" },
            layout: { className: "date-field-ltr" },
          }}
        />
        <Typography variant="body2" fontWeight={600} color="primary">
          מצטרפים חדשים בטווח:{" "}
          {isLoading ? "…" : (data?.newVolunteersInRange ?? 0).toLocaleString("he-IL")}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560 }}>
        מגדר/גיל: מתנדבים (שיוך VOLUNTEER, גם אם יש גם REQUESTING) עם תאריך לידה. מצטרפים חדשים:
        שיוכי מתנדב שנוצרו בטווח התאריכים. חזרת מתנדבים: לכל מתנדב נספרות השתתפויות
        מאושרות (APPROVED) בטווח - הגרף מפלח לפי מספר ההשתתפויות (0, 1, …, 6+). סכום
        העוגה שווה למספר המתנדבים באותה אוכלוסייה.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
          alignItems: "start",
          width: "100%",
        }}
      >
        <Paper elevation={1} sx={cardSx}>
          <KpiPieChart title="מגדר" data={genderPie} emptyLabel="אין נתונים" />
        </Paper>
        <Paper elevation={1} sx={cardSx}>
          <KpiPieChart title="גיל" data={agePie} emptyLabel="אין נתונים" />
        </Paper>
        <Paper elevation={1} sx={cardSx}>
          <KpiPieChart
            title="חזרת מתנדבים (לפי מספר השתתפויות בטווח)"
            data={repeatPie}
            emptyLabel="אין נתונים"
          />
        </Paper>
      </Box>
    </Stack>
  );
};

